<!-- 商品发布 - 物流设置 -->
<template>
  <el-form ref="formRef" :model="formData" :rules="rules" label-width="120px" :disabled="isDetail">
    <el-form-item label="配送方式" prop="deliveryTypes">
      <el-checkbox-group v-model="formData.deliveryTypes" class="w-80">
        <el-checkbox
          v-for="dict in getIntDictOptions(DICT_TYPE.TRADE_DELIVERY_TYPE)"
          :key="dict.value"
          :value="dict.value"
        >
          {{ dict.label }}
        </el-checkbox>
      </el-checkbox-group>
    </el-form-item>
    <el-form-item
      label="运费模板"
      prop="deliveryTemplateId"
      v-if="!skipTemplate && formData.deliveryTypes?.includes(DeliveryTypeEnum.EXPRESS.type)"
    >
      <el-select placeholder="请选择运费模板" v-model="formData.deliveryTemplateId" class="w-80">
        <el-option
          v-for="item in deliveryTemplateList"
          :key="item.id"
          :label="item.name"
          :value="item.id!"
        />
      </el-select>
    </el-form-item>
    <el-alert
      v-if="skipTemplate && formData.deliveryTypes?.includes(DeliveryTypeEnum.EXPRESS.type)"
      type="info"
      :closable="false"
      show-icon
      class="mb-12px"
      title="运费模板按店铺隔离，将在平台/店铺选品挂店时选择。"
    />
  </el-form>
</template>
<script lang="ts" setup>
import { PropType } from 'vue'
import { copyValueToTarget } from '@/utils'
import { propTypes } from '@/utils/propTypes'
import type { Spu } from '@/api/mall/product/spu'
import * as AdminExpressTemplateApi from '@/api/mall/trade/delivery/expressTemplate'
import * as MerchantExpressTemplateApi from '@/api/merchant/delivery/expressTemplate'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { DeliveryTypeEnum } from '@/utils/constants'

defineOptions({ name: 'ProductDeliveryForm' })

const message = useMessage() // 消息弹窗

const props = defineProps({
  propFormData: {
    type: Object as PropType<Spu>,
    default: () => {}
  },
  isDetail: propTypes.bool.def(false), // 是否作为详情组件
  merchant: propTypes.bool.def(false), // 是否商家端
  /** 供应商提报：不选店铺运费模板 */
  skipTemplate: propTypes.bool.def(false)
})
const skipTemplate = computed(() => props.skipTemplate)
const ExpressTemplateApi = computed(() =>
  props.merchant ? MerchantExpressTemplateApi : AdminExpressTemplateApi
)
const formRef = ref() // 表单 Ref
const formData = reactive<Spu>({
  deliveryTypes: [], // 配送方式
  deliveryTemplateId: undefined // 运费模版
})
const rules = computed(() => ({
  deliveryTypes: [{ required: true, message: '请选择配送方式', trigger: 'change' }],
  ...(props.skipTemplate || !formData.deliveryTypes?.includes(DeliveryTypeEnum.EXPRESS.type)
    ? {}
    : { deliveryTemplateId: [{ required: true, message: '请选择运费模板', trigger: 'change' }] })
}))

/** 将传进来的值赋值给 formData */
watch(
  () => props.propFormData,
  (data) => {
    if (!data) {
      return
    }
    copyValueToTarget(formData, data)
  },
  {
    immediate: true
  }
)

/** 表单校验 */
const emit = defineEmits(['update:activeName'])
const validate = async () => {
  if (!formRef) return
  try {
    await unref(formRef)?.validate()
    if (props.skipTemplate) {
      formData.deliveryTemplateId = undefined
    }
    // 校验通过更新数据
    Object.assign(props.propFormData, formData)
  } catch (e) {
    message.error('【物流设置】不完善，请填写相关信息')
    emit('update:activeName', 'delivery')
    throw e // 目的截断之后的校验
  }
}
defineExpose({ validate })

/** 初始化 */
const deliveryTemplateList = ref<AdminExpressTemplateApi.DeliveryExpressTemplateVO[]>([]) // 运费模版
onMounted(async () => {
  if (props.skipTemplate) {
    return
  }
  deliveryTemplateList.value = await ExpressTemplateApi.value.getSimpleTemplateList()
})
</script>
